import * as React from 'react';
import { BreakpointKey, ResponsiveStyleValue } from '../../theming';
import { Stacker, StackerProps } from '../Stacker/Stacker';
import { StackerDirection } from '../Stacker/Stacker.types';
import { PriceBlockAlign } from './PriceBlock.types';
import { PriceBlockAmount } from './PriceBlockAmount';
import { PriceBlockCaption } from './PriceBlockCaption';
import { PriceBlockContext } from './PriceBlockContext';
import { PriceBlockLegend } from './PriceBlockLegend';
import { PriceBlockWasAmount } from './PriceBlockWasAmount';

const alignToDirection: { [key in PriceBlockAlign]: 'row' | 'row-reverse' } = {
    start: 'row-reverse',
    end: 'row',
};

const convertAlignToDirection = (
    align: ResponsiveStyleValue<PriceBlockAlign>
): ResponsiveStyleValue<StackerDirection> => {
    if (typeof align === 'object') {
        const keys = Object.keys(align) as BreakpointKey[];
        return keys.reduce<{ [key in BreakpointKey]?: 'row' | 'row-reverse' }>((acc, breakpoint) => {
            const value = align[breakpoint] as PriceBlockAlign;
            acc[breakpoint] = alignToDirection[value];
            return acc;
        }, {});
    }
    return alignToDirection[align];
};

export interface PriceBlockProps extends StackerProps {
    /**
     * @default end
     */
    align?: ResponsiveStyleValue<PriceBlockAlign>;

    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * @default m
     */
    size?: 's' | 'm' | 'l';
}

export function PriceBlock(props: Readonly<PriceBlockProps>): React.JSX.Element {
    const { children, align = 'end', size = 'm', ref, ...other } = props;

    let caption;
    let amount;
    let wasAmount;
    let legend;

    React.Children.forEach(children, (element) => {
        switch (React.isValidElement(element) && element.type) {
            case PriceBlockCaption:
                caption = element;
                break;
            case PriceBlockWasAmount:
                wasAmount = element;
                break;
            case PriceBlockAmount:
                amount = element;
                break;
            case PriceBlockLegend:
                legend = element;
                break;
            default:
                break;
        }
    });

    const values = React.useMemo(
        () => ({
            size,
            align,
        }),
        [size, align]
    );

    return (
        <PriceBlockContext.Provider value={values}>
            <Stacker ref={ref} spacing={0.5} alignItems={align} {...other}>
                {caption && caption}
                <Stacker
                    direction={convertAlignToDirection(align)}
                    alignItems="baseline"
                    justifyContent={align}
                    flexWrap="wrap"
                    rowSpacing={0.5}
                    columnSpacing={2}
                >
                    {wasAmount && wasAmount}
                    {amount && amount}
                </Stacker>
                {legend && legend}
            </Stacker>
        </PriceBlockContext.Provider>
    );
}
